<template>
  <div class="flex items-center justify-center mt-14 min-h-full">
    <!-- Artsai Pixel -->
    <ConversionPixel
      :has-pixel="true"
      pixel="https://data.adxcel-ec2.com/pixel/?ad_log=referer&action=registration&order_id="
    />
    <!-- AudioHook Pixel -->
    <ConversionPixel
      pixel="https://listen.audiohook.com/b4d81c66-0b35-41e6-9c8c-9d5de456305b/pixel.png?type=form&order="
    />
    <!-- Capterra Pixel -->
    <ConversionPixel
      :track-user="false"
      pixel="https://ct.capterra.com/capterra_tracker.gif?vid=2206774&vkey=2a84d4318d5209d86a0b7919aea66aad"
    />
    <!-- Claritas Pixel -->
    <ConversionPixel
      pixel="https://trkn.us/pixel/conv/ppt=19232;g=lead;gid=44724;ord="
    />
    <RegistrationContainer>
      <div
        class="relative p-4 sm:w-96 sm:p-0 sm:pb-12 sm:mx-auto sm:flex sm:flex-col sm:justify-center"
      >
        <PurpleHexContainer
          v-if="!hasTier"
          class="flex flex-col px-6 my-20 sm:rounded-t-lg sm:rounded-b-lg justify-center"
        >
          <form class="my-4" @submit="handleSubmit">
            <TypeDisplay
              variant="mega"
              class="flex justify-center text-center mt-2 mb-4 text-white"
            >
              How many employees does your business have?
            </TypeDisplay>
            <EmployeeDropdown
              class="mb-4 text-center"
              :is-invalid="isInvalid"
              :size="$matchMedia.md ? 'normal' : 'large'"
              placeholder="Number of employees"
              data-qa="employee-dropdown"
              @input="handleInput"
            />
            <BaseButton
              :id="buttonId"
              variant="secondary"
              size="large"
              class="mb-4"
              block
              type="submit"
              >Continue</BaseButton
            >
          </form>
        </PurpleHexContainer>
        <template v-if="!showCountdown">
          <div v-if="hasTier && hasCompanyId" class="mt-4">
            <TypeDisplay
              tag="h1"
              class="text-center my-8 hidden sm:block"
              variant="tera"
            >
              The sooner we chat, the faster we can help.
              <br />Let's talk!
            </TypeDisplay>
            <template v-if="!isLoading">
              <div
                class="flex justify-center items-center"
                :class="showCallMeNowButton ? 'call-now' : 'call-later'"
                data-qa="call-me-now-button"
              >
                <CallMeNowButton
                  v-if="showCallMeNowButton"
                  @call="handleCallMeNow"
                />
                <BaseButton
                  v-else
                  id="schedule-call-later"
                  variant="inverted"
                  size="large"
                  @click="handleContinue"
                  >Schedule Call Later</BaseButton
                >
              </div>
              <BaseDivider class="my-6" dark label="OR Whenever You’re Ready" />
            </template>
          </div>
          <div v-if="hasTier">
            <client-only>
              <CalendlyEmbed
                class="shadow-3 rounded-lg border border-gray-4 p-1 bg-white"
                :class="{ 'mt-12': !hasCompanyId }"
                :url="calendlyURL"
                @event-scheduled="handleContinue"
              />
            </client-only>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col text-center">
            <CallMeNowImage class="mx-auto mb-9" />
            <TypeDisplay tag="h1" class="text-23 font-bold mb-8"
              >A Bambee Representative Will Call You In:</TypeDisplay
            >
            <div class="self-center bg-gray-6 rounded-lg py-3 px-6 mb-8">
              <TypeDisplay>{{ countdownString }}</TypeDisplay>
            </div>
            <BaseButton
              class="self-center"
              size="large"
              variant="secondary"
              data-qa="will-call-now-continue"
              @click="handleContinue"
              >Continue</BaseButton
            >
          </div>
        </template>
      </div>
    </RegistrationContainer>
  </div>
</template>

<script>
import {
  BaseButton,
  BaseDivider,
  IconButton,
  TypeDisplay,
} from '@bambeehr/pollen';
import {
  computed,
  defineComponent,
  ref,
  useRoute,
  onMounted,
} from '@nuxtjs/composition-api';
import getDay from 'date-fns/getDay';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';
import isWithinInterval from 'date-fns/isWithinInterval';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import CallMeNowButton from '@/modules/SelfServiceRegistration/components/CallMeNowButton';
import RegistrationContainer from '@/modules/SelfServiceRegistration/components/RegistrationContainer';
import Contentful, { ContentfulEntryIDs } from '@/services/Contentful';
import EmployeeDropdown from '@/components/EmployeeDropdown.vue';

import Benefits from '@/constants/Benefits';
import CurrentUser from '@/services/CurrentUser';
import useCurrentUser from '@/hooks/useCurrentUser';
import useRegistrationSummary from '@/hooks/useRegistrationSummary';
import useSelfService from '@/modules/SelfServiceRegistration/hooks/useSelfService';
import CallMeNowImage from '@/assets/images/self-service-checkout/call-me-now.svg';

import CalendlyEmbed from '@/components/CalendlyEmbed/CalendlyEmbed.vue';
import usePricePlans from '@/hooks/usePricePlans';
import {
  generateCalendlyURL,
  generateSalesTeamCalendlySlug,
  getCalendlySlug,
} from '@/utils/calendlyHelpers';
import useMatchMedia from '@/hooks/useMatchMedia';
import useRegistration, {
  RegistrationStateNames,
} from '@/modules/SelfServiceRegistration/hooks/useRegistration';
import { getFromLocalStorage } from '@/utils/localStorage';
import PurpleHexContainer from '@/components/PurpleHexContainer.vue';
import * as shortid from 'shortid';
import ConversionPixel from '@/components/Marketing/ConversionPixel.vue';
import { error as DDError } from '@/services/Datadog'

const COUNTDOWN_SECONDS = 60;

const buttonId = 'company-size-button';
const SCHEDULE_CALL_FLOW = 'schedule-call-landing-page';

const mapHoliday = (holiday) => {
  const { startDate, endDate } = holiday.fields;

  return {
    start: new Date(startDate),
    end: new Date(endDate),
  };
};

const getPacificTime = () => utcToZonedTime(new Date(), 'America/Los_Angeles');

const currentTime = getPacificTime();

// Timeslots are strings with two 24-hour formatted times given in Pacific time
// separated by a hyphen, e.g. 06:00-18:00. We can parse this to make bounding
// dates.
export const isWithinTimeslot = (date, timeslot) => {
  const [start, end] = timeslot?.split('-').map((time) => {
    const [hours, minutes] = time.split(':');

    return setHours(setMinutes(date, minutes), hours);
  });

  return isWithinInterval(date, {
    start,
    end,
  });
};

const testTimeslot = (timeslot) => isWithinTimeslot(currentTime, timeslot);

const fetchAvailability = async () => {
  let result;
  try {
    const { fields } = await Contentful.getEntry(
      ContentfulEntryIDs.SALES_CALL_AVAILABILITY
    );
    const {
      salesHolidays = [],
      sunday = [],
      monday = [],
      tuesday = [],
      wednesday = [],
      thursday = [],
      friday = [],
      saturday = [],
    } = fields;

    result = {
      holidays: salesHolidays.map(mapHoliday),
      schedule: [
        sunday,
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
      ],
    };
  } catch (err) {
    result = null;
  }

  return result;
};

export default defineComponent({
  name: 'schedule-call-page',
  components: {
    BaseButton,
    BaseDivider,
    CalendlyEmbed,
    CallMeNowButton,
    CallMeNowImage,
    IconButton,
    RegistrationContainer,
    TypeDisplay,
    EmployeeDropdown,
    PurpleHexContainer,
    ConversionPixel,
  },

  setup() {
    const { registrationSummary } = useRegistrationSummary();
    const { plans } = usePricePlans();
    const { currentUser } = useCurrentUser();
    const { goToNextPage } = useSelfService();
    const matchMedia = useMatchMedia();
    const route = useRoute();
    const salesTeamSlug = ref();

    const { company, companyId, industry, user } = currentUser;
    const { state, updateRegistration } = useRegistration();

    const calendlyURL = ref('');
    const focus = ref(null);
    const showCountdown = ref(false);
    const items = ref(Benefits);
    const utms = ref();
    const seconds = ref(COUNTDOWN_SECONDS);
    let timer;
    const query = computed(() => route.value?.query);
    const tier = ref(query.value?.tier);
    const hasTier = ref(!!query.value.tier);
    const name = computed(() => query.value?.name || state.fullName);
    const email = computed(() => query.value?.email || state.email);
    const rep = computed(() => query.value?.rep);
    const hasCompanyId = computed(
      () => !!query.value.compid || !!state.companyId || !!companyId
    );

    const utmContent = computed(() => {
      const utmContentArray = [
        state.companyId,
        state.userId,
        shortid.generate(),
        'overview-call',
      ];

      return utmContentArray.join('|');
    });

    const isInvalid = ref(false);

    const convertUtms = () => {
      const utmList = Object.entries(query.value);
      utms.value = utmList
        .filter(([key]) => key.startsWith('utm_'))
        .map(([key, value]) => `&${key}=${value}`)
        .join('');
    };

    const handleInput = () => {
      isInvalid.value = false;
    };

    const generateCalendly = () => {
      convertUtms();

      if (hasCompanyId.value) {
        salesTeamSlug.value = generateSalesTeamCalendlySlug(
          company,
          plans.value,
          currentUser
        );
        calendlyURL.value = generateCalendlyURL({
          guest: user,
          slug: salesTeamSlug.value,
          query: query.value,
        });
      }

      if (tier.value === 'undefined') {
        hasTier.value = null;
      }
      salesTeamSlug.value = getCalendlySlug(tier.value, company, plans.value);
      calendlyURL.value = `https://calendly.com/${
        salesTeamSlug.value
      }?primary_color=68478d&name=${name.value || ''}&email=${
        email.value || ''
      }&hide_event_type_details=1&hide_landing_page_details=1&hide_gdpr_banner=1${
        utms.value || ''
      }&utm_content=${utmContent.value}&utm_term=www|${process.env.ENV}`;
    };

    onMounted(() => {
      updateRegistration({
        [RegistrationStateNames.COMPANY_ID]:
          query.value?.compid || state.companyId,
        [RegistrationStateNames.USER_ID]: query.value?.uid || state.userId,
        name: query.value.name,
        email: query.value.email,
      });
      if (rep.value?.length > 1) {
        calendlyURL.value = `https://calendly.com/${rep.value}/?name=${name.value}&email=${email.value}&hide_event_type_details=1&hide_landing_page_details=1&hide_gdpr_banner=1${utms.value}&utm_content=${utmContent.value}&utm_term=www|${process.env.ENV}`;
        hasTier.value = true;
      }
      if (!calendlyURL.value) {
        generateCalendly();
      }
    });

    const handleSubmit = () => {
      if (!state.numOfEmployees) {
        isInvalid.value = true;

        return;
      }
      updateRegistration({
        [RegistrationStateNames.FUNNEL]: SCHEDULE_CALL_FLOW,
      });
      const newTier = getFromLocalStorage('ssu-plan');
      tier.value = newTier.slice(-1);
      generateCalendly();
      hasTier.value = true;
    };

    const infoDescription = computed(
      () =>
        `The bad news is, the ${industry} industry must comply with specific HR compliance standards. <b>The good news</b> is we've got you covered. <p class="font-bold mt-2">The sooner we chat, the faster we can help. Let's talk!</p>`
    );

    const infoTitle = computed(() => `We know ${industry}`);

    const startCountdown = () => {
      timer = setInterval(() => {
        if (seconds.value > 0) {
          seconds.value -= seconds.value;
        }
      }, 1000);
    };

    const handleCallMeNow = async () => {
      try {
        await CurrentUser.callMeNow(companyId);
        if (!timer) {
          startCountdown();
        }
      } catch (err) {
        DDError('Error with call me now', err);
      } finally {
        showCountdown.value = true;
      }
    };

    const countdownString = computed(() => {
      /**
       * toLocaleString to show 0 in front of numbers 1-9
       * eg. format: 00:01 vs 00:1
       */
      return `00:${seconds.value.toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}`;
    });

    const handleContinue = () => {
      goToNextPage();
    };

    const toggleLogo = computed(
      () => matchMedia.value.xl || !matchMedia.value.lg
    );

    return {
      calendlyURL,
      countdownString,
      focus,
      infoDescription,
      infoTitle,
      items,
      registrationSummary,
      showCountdown,
      handleCallMeNow,
      handleContinue,
      toggleLogo,
      hasTier,
      buttonId,
      handleInput,
      handleSubmit,
      isInvalid,
      hasCompanyId,
    };
  },
  data() {
    return {
      callAvailability: null,
      isLoading: true,
    };
  },

  computed: {
    isHoliday() {
      if (!this.callAvailability) {
        return false;
      }

      return this.callAvailability.holidays.some(({ start, end }) =>
        isWithinInterval(this.currentTime, {
          start,
          end,
        })
      );
    },

    showCallMeNowButton() {
      if (!this.callAvailability || this.isHoliday) {
        return false;
      }

      // The availability schedule is an array of arrays, where the root array
      // corresponds to the days of the week, and their children are arrays of
      // timeslot strings. First we want to grab the timeslot array that matches
      // this day of the week.
      const timeslots = this.callAvailability?.schedule[getDay(currentTime)];

      // Return true if the current time falls within any timeslot.
      return timeslots.some(testTimeslot);
    },
  },

  async created() {
    this.isLoading = true;
    this.callAvailability = await fetchAvailability();
    this.isLoading = false;
  },
});
</script>
